import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, ChevronLeft, ChevronRight } from '../ui/Icon';

const EDGE_TOLERANCE = 4;

interface Props {
  children: ReactNode;
  /** Pinned label to the left of the chips. Defaults to "Filter". */
  label?: string;
}

/** Bottom bar shell: pinned label + horizontally scrollable chips with edge arrows. */
export default function MapFilterBar({ children, label }: Props) {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canLeft, setCanLeft]   = useState(false);
  const [canRight, setCanRight] = useState(false);

  const syncEdges = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > EDGE_TOLERANCE);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - EDGE_TOLERANCE);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    syncEdges();
    const resizeObserver = new ResizeObserver(syncEdges);
    resizeObserver.observe(el);
    // Chips come and go as venues/days change; MutationObserver keeps the arrows honest.
    const mutationObserver = new MutationObserver(syncEdges);
    mutationObserver.observe(el, { childList: true, subtree: true });
    return () => { resizeObserver.disconnect(); mutationObserver.disconnect(); };
  }, [syncEdges]);

  function scrollStep(direction: -1 | 1) {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * Math.max(160, el.clientWidth * 0.6), behavior: 'smooth' });
  }

  return (
    <div className="shrink-0 relative bg-white dark:bg-slate-900 border-t border-slate-100 dark:border-slate-800 flex items-center gap-2 px-4 py-2">
      <span className="text-[11px] font-medium text-slate-400 dark:text-slate-500 shrink-0">{label ?? t('map.filter')}:</span>

      <div className="relative flex-1 min-w-0">
        <div ref={scrollRef} onScroll={syncEdges}
          className="flex items-center gap-2 overflow-x-auto scrollbar-none scroll-smooth">
          {children}
        </div>

        {canLeft && (
          <ArrowButton side="left" label={t('map.scrollLeft')} onClick={() => scrollStep(-1)} />
        )}
        {canRight && (
          <ArrowButton side="right" label={t('map.scrollRight')} onClick={() => scrollStep(1)} />
        )}
      </div>
    </div>
  );
}

function ArrowButton({ side, label, onClick }: { side: 'left' | 'right'; label: string; onClick: () => void }) {
  const isLeft = side === 'left';
  return (
    <div className={`absolute top-0 bottom-0 flex items-center pointer-events-none ${
      isLeft
        ? 'left-0 pr-6 bg-gradient-to-r from-white dark:from-slate-900 to-transparent'
        : 'right-0 pl-6 bg-gradient-to-l from-white dark:from-slate-900 to-transparent'
    }`}>
      <button type="button" onClick={onClick} title={label} aria-label={label}
        className="pointer-events-auto flex items-center justify-center w-6 h-6 rounded-full border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400 shadow-sm hover:text-slate-800 dark:hover:text-slate-200 hover:border-slate-300 dark:hover:border-slate-600 transition-colors cursor-pointer">
        <Icon as={isLeft ? ChevronLeft : ChevronRight} size={14} />
      </button>
    </div>
  );
}
